import { useState, useEffect, useCallback } from 'react';

/**
 * Hook personnalisé pour récupérer des données depuis PocketBase
 * @param {Function} fetchFunction - Fonction de récupération des données
 * @param {Array} dependencies - Dépendances pour relancer le fetch
 * @returns {Object} { data, loading, error, refetch }
 */
export function usePocketBaseCollection(fetchFunction, dependencies = []) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await fetchFunction();
            setData(result);
        } catch (err) {
            console.error('Erreur lors de la récupération des données:', err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [fetchFunction]);

    useEffect(() => {
        fetchData();
    }, [fetchData, ...dependencies]);

    return { data, loading, error, refetch: fetchData };
}

/**
 * Hook pour gérer les opérations CRUD (Create, Read, Update, Delete)
 * @returns {Object} { create, update, delete, loading, error }
 */
export function usePocketBaseMutation() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const executeMutation = async (mutationFunction, onSuccess) => {
        setLoading(true);
        setError(null);
        try {
            const result = await mutationFunction();
            if (onSuccess) {
                onSuccess(result);
            }
            return result;
        } catch (err) {
            console.error('Erreur lors de la mutation:', err);
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    return { executeMutation, loading, error };
}

/**
 * Hook pour gérer l'authentification
 * @returns {Object} { user, isAuthenticated, login, logout, loading }
 */
export function usePocketBaseAuth() {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Vérifier l'authentification au montage
    useEffect(() => {
        const checkAuth = async () => {
            try {
                const pb = (await import('../services/pocketbase')).default;
                if (pb.authStore.isValid) {
                    setUser(pb.authStore.model);
                    setIsAuthenticated(true);
                }
            } catch (err) {
                console.error('Erreur de vérification auth:', err);
            }
        };
        checkAuth();
    }, []);

    const login = async (email, password) => {
        setLoading(true);
        setError(null);
        try {
            const { loginAdmin } = await import('../services/pocketbase');
            const authData = await loginAdmin(email, password);
            setUser(authData.record);
            setIsAuthenticated(true);
            return authData;
        } catch (err) {
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            const { logout: pbLogout } = await import('../services/pocketbase');
            pbLogout();
            setUser(null);
            setIsAuthenticated(false);
        } catch (err) {
            console.error('Erreur de déconnexion:', err);
        }
    };

    return { user, isAuthenticated, login, logout, loading, error };
}

/**
 * Hook pour gérer les souscriptions en temps réel
 * @param {string} collectionName - Nom de la collection
 * @param {Function} onUpdate - Callback appelé lors d'une mise à jour
 * @returns {Object} { subscribed, error }
 */
export function usePocketBaseRealtime(collectionName, onUpdate) {
    const [subscribed, setSubscribed] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let pb;
        const setupSubscription = async () => {
            try {
                pb = (await import('../services/pocketbase')).default;

                await pb.collection(collectionName).subscribe('*', (e) => {
                    if (onUpdate) {
                        onUpdate(e);
                    }
                });

                setSubscribed(true);
            } catch (err) {
                console.error('Erreur de souscription:', err);
                setError(err);
            }
        };

        setupSubscription();

        return () => {
            if (pb) {
                pb.collection(collectionName).unsubscribe('*').catch(console.error);
            }
        };
    }, [collectionName, onUpdate]);

    return { subscribed, error };
}

/**
 * Hook pour gérer la pagination
 * @param {Function} fetchFunction - Fonction pour récupérer les données
 * @param {number} pageSize - Nombre d'éléments par page
 * @returns {Object} { data, page, totalPages, loading, error, nextPage, prevPage, goToPage }
 */
export function usePocketBasePagination(fetchFunction, pageSize = 20) {
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchPage = useCallback(async (pageNumber) => {
        setLoading(true);
        setError(null);
        try {
            const result = await fetchFunction(pageNumber, pageSize);
            setData(result.items || []);
            setTotalPages(result.totalPages || 1);
            setTotalItems(result.totalItems || 0);
            setPage(pageNumber);
        } catch (err) {
            console.error('Erreur de pagination:', err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [fetchFunction, pageSize]);

    useEffect(() => {
        fetchPage(1);
    }, [fetchPage]);

    const nextPage = () => {
        if (page < totalPages) {
            fetchPage(page + 1);
        }
    };

    const prevPage = () => {
        if (page > 1) {
            fetchPage(page - 1);
        }
    };

    const goToPage = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            fetchPage(pageNumber);
        }
    };

    return {
        data,
        page,
        totalPages,
        totalItems,
        loading,
        error,
        nextPage,
        prevPage,
        goToPage,
        refetch: () => fetchPage(page)
    };
}
